import { z } from 'zod';

// All supported block types in Release-1
export const BlockTypeEnum = z.enum([
  'text',
  'heading',
  'quote',
  'list',
  'image',
  'video',
  'audio',
  'divider',
  'callout',
  'external_link',
]);

export type BlockType = z.infer<typeof BlockTypeEnum>;
export const BlockVisibilityEnum = z.enum(['published', 'hidden']);
export type BlockVisibility = z.infer<typeof BlockVisibilityEnum>;
export const VideoBlockStyleEnum = z.enum(['regular', 'round']);
export type VideoBlockStyle = z.infer<typeof VideoBlockStyleEnum>;
export const AudioBlockStyleEnum = z.enum(['voice']);
export type AudioBlockStyle = z.infer<typeof AudioBlockStyleEnum>;
export const AudioTelegramSourceTypeEnum = z.enum(['voice']);
export type AudioTelegramSourceType = z.infer<typeof AudioTelegramSourceTypeEnum>;

// Per-type payload schemas for validation
const TextPayloadSchema = z.object({ text: z.string().min(1) }).strict();
const HeadingPayloadSchema = z.object({
  text: z.string().min(1),
  level: z.number().int().min(1).max(3).default(2),
});
const QuotePayloadSchema = z.object({
  text: z.string().min(1),
  author: z.string().optional(),
});
const ListPayloadSchema = z.object({
  items: z.array(z.string().min(1)).min(1),
  ordered: z.boolean().default(false),
});
const BaseMediaPayloadSchema = z.object({
  assetId: z.string().uuid().optional(),
  caption: z.string().max(1024).optional(),
});
const VideoPayloadSchema = BaseMediaPayloadSchema.extend({
  style: VideoBlockStyleEnum.optional(),
});
const AudioPayloadSchema = BaseMediaPayloadSchema.extend({
  style: AudioBlockStyleEnum.optional(),
  telegramSourceType: AudioTelegramSourceTypeEnum.optional(),
});
const DividerPayloadSchema = z.object({});
const CalloutPayloadSchema = z
  .object({
    text: z.string().min(1),
    emoji: z.string().max(4).optional(),
  })
  .strict();
const ExternalLinkPayloadSchema = z.object({
  url: z.string().url(),
  label: z.string().max(200).optional(),
});

export const BLOCK_PAYLOAD_SCHEMAS: Record<BlockType, z.ZodTypeAny> = {
  text: TextPayloadSchema,
  heading: HeadingPayloadSchema,
  quote: QuotePayloadSchema,
  list: ListPayloadSchema,
  image: BaseMediaPayloadSchema,
  video: VideoPayloadSchema,
  audio: AudioPayloadSchema,
  divider: DividerPayloadSchema,
  callout: CalloutPayloadSchema,
  external_link: ExternalLinkPayloadSchema,
};

export function validateBlockPayload(
  type: BlockType,
  payload: unknown,
): { ok: true; data: unknown } | { ok: false; errors: Record<string, string[]> } {
  const schema = BLOCK_PAYLOAD_SCHEMAS[type];
  const result = schema.safeParse(payload);
  if (result.success) {
    return { ok: true, data: result.data };
  }
  const errors: Record<string, string[]> = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.') || 'payload';
    if (!errors[path]) errors[path] = [];
    errors[path]!.push(issue.message);
  }
  return { ok: false, errors };
}

export const AdminCreateBlockSchema = z.object({
  index: z.number().int().min(1),
  type: BlockTypeEnum,
  payload: z.record(z.unknown()),
  mediaAssetId: z.string().uuid().optional(),
});

export type AdminCreateBlockDto = z.infer<typeof AdminCreateBlockSchema>;

export const AdminUpdateBlockSchema = z.object({
  index: z.number().int().min(1).optional(),
  type: BlockTypeEnum.optional(),
  payload: z.record(z.unknown()).optional(),
  mediaAssetId: z.string().uuid().nullable().optional(),
  visibility: BlockVisibilityEnum.optional(),
});

export type AdminUpdateBlockDto = z.infer<typeof AdminUpdateBlockSchema>;

export const AdminBlockResponseSchema = z.object({
  id: z.string(),
  chapterId: z.string(),
  index: z.number(),
  visibility: BlockVisibilityEnum,
  type: z.string(),
  payload: z.record(z.unknown()),
  mediaAssetId: z.string().nullable(),
  mediaUrl: z.string().nullable(),
  mediaMimeType: z.string().nullable().optional(),
  mediaProcessingStatus: z.enum(['ready', 'processing', 'failed']).nullable().optional(),
  updatedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AdminBlockResponse = z.infer<typeof AdminBlockResponseSchema>;

const AdminBulkBlockIdsSchema = z
  .array(z.string().uuid())
  .min(1)
  .max(100)
  .refine((blockIds) => new Set(blockIds).size === blockIds.length, 'Block IDs must be unique');

export const AdminBulkBlockActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('delete'),
    blockIds: AdminBulkBlockIdsSchema,
  }),
  z.object({
    action: z.literal('hide'),
    blockIds: AdminBulkBlockIdsSchema,
  }),
  z.object({
    action: z.literal('show'),
    blockIds: AdminBulkBlockIdsSchema,
  }),
  z.object({
    action: z.literal('move'),
    blockIds: AdminBulkBlockIdsSchema,
    targetChapterId: z.string().uuid(),
  }),
]);

export type AdminBulkBlockActionDto = z.infer<typeof AdminBulkBlockActionSchema>;

export const AdminBulkBlockActionResultSchema = z.object({
  action: z.enum(['delete', 'hide', 'show', 'move']),
  sourceChapterId: z.string().uuid(),
  targetChapterId: z.string().uuid().nullable(),
  blockIds: z.array(z.string().uuid()),
  affectedCount: z.number().int().min(0),
});

export type AdminBulkBlockActionResult = z.infer<typeof AdminBulkBlockActionResultSchema>;
